import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { chamarApi, type ErroApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

/**
 * A camada de dados do calendário de dias letivos por DRE e mês.
 *
 * Dado real, da API: `bff/v1/admin/dias-letivos` (módulo `dias-letivos`, tabela
 * `dias_letivos`, migração 0310) e, para as linhas da grade, `GET /financeiro/dres`,
 * que já existia. Nenhum componente chama `chamarApi` direto, chave de cache junto
 * de quem a usa, `retry: naoInsistirEmRecusa` em toda consulta (CLAUDE.md, "Regras
 * de API"). Toda regra — total dentro do mês, datas, feriados sem repetição,
 * concorrência por versão — é conferida no banco.
 */

export const CAMINHO_DA_API = '/bff/v1/admin/dias-letivos';
export const CAMINHO_DAS_DRES = '/financeiro/dres';

/** Uma Diretoria Regional de Educação, como `GET /financeiro/dres` a devolve. */
export interface Dre {
  id: string;
  codigo: string | null;
  nome: string | null;
}

/**
 * Os dias letivos de uma DRE num mês.
 *
 * `mesReferencia` é o primeiro dia do mês (`AAAA-MM-01`). `datasLetivas` vem
 * vazia quando só o total foi informado; havendo datas, o total é o número
 * delas. `versao` é a que a gravação seguinte precisa devolver.
 */
export interface DiasLetivosDoMes {
  id: string;
  dreId: string;
  mesReferencia: string;
  totalDiasLetivos: number;
  datasLetivas: string[];
  feriados: string[];
  observacoes: string | null;
  versao: number;
  atualizadoEm: string;
}

export interface DiasLetivosDoAno {
  ano: number;
  itens: DiasLetivosDoMes[];
}

/** O que a tela manda ao gravar um mês. A DRE e o mês vão no endereço. */
export interface EntradaDoMes {
  totalDiasLetivos: number;
  feriados: string[];
  observacoes: string | null;
  /** Ausente na primeira gravação do mês; obrigatória depois. */
  versao?: number;
}

export const CHAVES_DE_DIAS_LETIVOS = {
  dres: ['admin', 'dias-letivos', 'dres'] as const,
  porAno: (ano: number) => ['admin', 'dias-letivos', 'ano', ano] as const,
  todosOsAnos: ['admin', 'dias-letivos', 'ano'] as const,
};

/** Os meses do ano, abreviados, na ordem das colunas da grade. */
export const MESES_ABREVIADOS = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
] as const;

/** Como a DRE aparece na linha da grade: o código, ou o nome se ela não tiver código. */
export function rotuloDaDre(dre: Dre): string {
  return dre.codigo?.trim() || dre.nome?.trim() || 'DRE';
}

/** As Diretorias Regionais de Educação, para as linhas da grade. */
export function useDres() {
  return useQuery<Dre[], ErroApi>({
    queryKey: CHAVES_DE_DIAS_LETIVOS.dres,
    queryFn: () => chamarApi<Dre[]>(CAMINHO_DAS_DRES),
    retry: naoInsistirEmRecusa,
  });
}

/** Os dias letivos de todas as DREs num ano. */
export function useDiasLetivosDoAno(ano: number) {
  return useQuery<DiasLetivosDoAno, ErroApi>({
    queryKey: CHAVES_DE_DIAS_LETIVOS.porAno(ano),
    queryFn: () => chamarApi<DiasLetivosDoAno>(`${CAMINHO_DA_API}?ano=${ano}`),
    retry: naoInsistirEmRecusa,
  });
}

/**
 * Grava um mês: cria se ainda não existe, altera se a versão enviada confere.
 * Em qualquer desfecho a grade do ano é lida de novo — num conflito, é ela que
 * traz a versão nova para a pessoa refazer a alteração.
 */
export function useGravarDiasLetivos() {
  const cliente = useQueryClient();
  return useMutation<
    DiasLetivosDoMes,
    ErroApi,
    { dreId: string; ano: number; mes: number; entrada: EntradaDoMes }
  >({
    mutationFn: ({ dreId, ano, mes, entrada }) =>
      chamarApi<DiasLetivosDoMes>(
        `${CAMINHO_DA_API}/${encodeURIComponent(dreId)}/${ano}-${String(mes + 1).padStart(2, '0')}`,
        { metodo: 'PUT', corpo: entrada },
      ),
    onSettled: () => cliente.invalidateQueries({ queryKey: CHAVES_DE_DIAS_LETIVOS.todosOsAnos }),
  });
}

/**
 * Indexa a grade por `dreId-mes` (mês de 0 a 11), que é como a tabela a lê.
 *
 * Lê o mês do texto `AAAA-MM-01` em vez de construir um `Date`, porque
 * `new Date('2026-03-01')` é interpretado em UTC e volta como fevereiro em
 * fuso negativo — o de São Paulo.
 */
export function indexarPorDreEMes(
  linhas: DiasLetivosDoMes[] | undefined,
): Record<string, DiasLetivosDoMes> {
  const indice: Record<string, DiasLetivosDoMes> = {};
  for (const linha of linhas ?? []) {
    const mes = Number(linha.mesReferencia.slice(5, 7)) - 1;
    indice[`${linha.dreId}-${mes}`] = linha;
  }
  return indice;
}

/** Quantos dias tem o mês (`mes` de 0 a 11). Dia 0 do mês seguinte é o último deste. */
export function diasDoMes(ano: number, mes: number): number {
  return new Date(ano, mes + 1, 0).getDate();
}

/** Do texto "Carnaval, Corpus Christi" para a lista: sem espaço nas pontas e sem item vazio. */
export function feriadosDoTexto(texto: string): string[] {
  return texto
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}
